import type * as THREE from "three";

/** Retain mixer output separately from later IK/additive writes. Restoring rest
 * every frame is NOT safe: Three's PropertyMixer can skip unchanged tracks. */
export class BasePose {
  private readonly values: Float64Array;
  constructor(private readonly bones: readonly THREE.Bone[]) {
    this.values = new Float64Array(bones.length * 10);
    this.capture();
  }
  capture(): void {
    for (let i = 0; i < this.bones.length; i++) {
      const bone = this.bones[i], offset = i * 10;
      bone.position.toArray(this.values, offset);
      bone.quaternion.toArray(this.values, offset + 3);
      bone.scale.toArray(this.values, offset + 7);
    }
  }
  /** Rig tuning changes proportions outside animation evaluation. Preserve the
   * sampled rotations/pelvis journey while refreshing persistent dimensions. */
  capturePlacement(): void {
    for (let i = 0; i < this.bones.length; i++) {
      const bone = this.bones[i], offset = i * 10;
      if (bone.name !== "pelvis") bone.position.toArray(this.values, offset);
      bone.scale.toArray(this.values, offset + 7);
    }
  }
  restore(): void {
    for (let i = 0; i < this.bones.length; i++) {
      const bone = this.bones[i], offset = i * 10;
      bone.position.fromArray(this.values, offset);
      bone.quaternion.fromArray(this.values, offset + 3);
      bone.scale.fromArray(this.values, offset + 7);
    }
  }
}

export type LegOwner = "mount" | "action" | "flight" | "jump" | "ground" | "animation";
export function legPoseOwner(mounted: boolean, authoredAction: boolean, flight: boolean, jump: boolean, grounded: boolean, swimming = false): LegOwner {
  if (mounted) return "mount";
  if (authoredAction) return "action";
  if (swimming) return "animation";
  if (flight) return "flight";
  if (jump) return "jump";
  return grounded ? "ground" : "animation";
}
